import { useEffect, useRef, useState } from "react";
import { usePhotoDropPaste } from "@/hooks/publish/usePhotoDropPaste";
import { SellerNetCards } from "@/components/publish/SellerNetCards";
import { imgUrl, money } from "./ui";

// Escritorio (≥1024 px) de P5, P6 y P7 · maqueta D2–D7
export const CORAL = "#FF4438";

export const DeskShell = ({ micro, a, b, left, right, testid }) => (
  <div className="max-w-[1280px] mx-auto px-12" data-testid={testid}>
    <div className="sx-mono text-[11px] tracking-[.18em] uppercase text-[#FF6B5E] mb-2">{micro}</div>
    <h1 className="font-extrabold tracking-[-.05em] leading-[.95] text-[56px] text-white mb-7">{a} <span className="italic font-normal text-white/60">{b}</span></h1>
    {right ? (
      <div className="grid gap-8" style={{ gridTemplateColumns: "minmax(0,1fr) 400px" }}>
        <div className="space-y-[18px] min-w-0">{left}</div>
        <aside className="sticky top-[88px] self-start rounded-[24px] p-[22px] space-y-4 bg-[#0d0d0d]" style={{ border: "1px solid rgba(255,68,56,.4)" }}>{right}</aside>
      </div>
    ) : left}
  </div>
);

export const DCard = ({ title, aside, children, tone, testid }) => (
  <section className="rounded-[22px] p-[22px] bg-[#101010] space-y-[18px]" style={{ border: `1px solid ${tone || "rgba(255,255,255,.1)"}` }} data-testid={testid}>
    {(title || aside) && <div className="flex items-center justify-between gap-3"><h2 className="text-[20px] font-bold text-white">{title}</h2>{aside}</div>}
    {children}
  </section>
);

export const Lbl = ({ children }) => <div className="sx-mono text-[10px] tracking-[.18em] uppercase text-white/45 mb-2">{children}</div>;
const ring = (v, err) => (err ? { borderColor: CORAL, background: "rgba(255,68,56,.06)" } : v ? { borderColor: "rgba(255,68,56,.7)", borderWidth: 1.5 } : {});
const box = "w-full h-[52px] rounded-[14px] bg-[#141414] border border-white/[.14] px-4 text-[15px] text-white placeholder:text-white/35 focus:outline-none";

export const DInput = ({ label, value, err, prefix, inputRef, ...p }) => (
  <label className="block min-w-0">
    {label && <Lbl>{label}</Lbl>}
    <span className={`${box} flex items-center gap-1.5`} style={ring(value !== "" && value != null, err)}>
      {prefix && <span className="text-white/60">{prefix}</span>}
      <input ref={inputRef} value={value} className="flex-1 min-w-0 bg-transparent focus:outline-none" {...p} />
    </span>
    {err && <div className="text-[12.5px] text-[#ff8a80] mt-1.5">{err}</div>}
  </label>
);

export const DSelect = ({ label, value, options, onChange, testid }) => (
  <label className="block min-w-0">
    {label && <Lbl>{label}</Lbl>}
    <select value={value} onChange={(e) => onChange(e.target.value)} className={box} style={ring(!!value)} data-testid={testid}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

export const DStepper = ({ label, value, onChange, testid }) => (
  <div className="min-w-0"><Lbl>{label}</Lbl>
    <div className={`${box} flex items-center justify-between`} style={ring(true)} data-testid={testid}>
      <button type="button" onClick={() => onChange(Math.max(1, value - 1))} className="text-white/70 text-lg w-8" data-testid={`${testid}-minus`}>−</button>
      <span className="font-bold">{value}</span>
      <button type="button" onClick={() => onChange(Math.min(99, value + 1))} className="text-[#FF6B5E] text-lg w-8" data-testid={`${testid}-plus`}>+</button>
    </div>
  </div>
);

export const Row = ({ cols = 3, children }) => <div className="grid gap-[14px]" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>{children}</div>;

export const TypeCards = ({ items, value, onChange, cols, testid }) => (
  <div className="grid gap-[14px]" style={{ gridTemplateColumns: `repeat(${cols || items.length}, minmax(0,1fr))` }}>
    {items.map((t) => (
      <button key={t.key} type="button" onClick={() => onChange(t.key)} data-testid={`${testid}-${t.key}`} className="text-left p-4 rounded-[16px] border transition-colors"
        style={value === t.key ? { borderColor: CORAL, borderWidth: 1.5, background: "rgba(255,68,56,.1)" } : { borderColor: "rgba(255,255,255,.1)" }}>
        <div className="text-[17px] font-bold text-white">{t.label}</div>
        {t.hint && <div className="text-[12.5px] text-white/50">{t.hint}</div>}
      </button>
    ))}
  </div>
);

export const CondPills = ({ items, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2">{items.map((c) => (
    <button key={c.key} type="button" onClick={() => onChange(c.key)} data-testid={`${testid}-${c.key}`} className="h-11 px-4 rounded-full border text-[14px]"
      style={value === c.key ? { borderColor: CORAL, background: "rgba(255,68,56,.12)", color: "#fff", fontWeight: 700 } : { borderColor: "rgba(255,255,255,.12)", color: "rgba(255,255,255,.7)" }}>{c.label}</button>
  ))}</div>
);

export const DPhotos = ({ files, setFiles, max = 10, rule, err, testid }) => {
  const drop = usePhotoDropPaste((fs) => setFiles((p) => [...p, ...fs].slice(0, max)));
  const inp = useRef(null);
  const [urls, setUrls] = useState([]);
  useEffect(() => { const u = files.map((f) => URL.createObjectURL(f)); setUrls(u); return () => u.forEach((x) => URL.revokeObjectURL(x)); }, [files]);
  const slots = Math.max(4, files.length + 1);
  return (
    <DCard title="Fotos" testid={testid}>
      <div className="flex justify-between -mt-2"><Lbl>Fotos</Lbl><span className="text-[12.5px]" style={{ color: err ? "#ff8a80" : "rgba(255,255,255,.5)" }} data-testid={`${testid}-rule`}>{err || rule}</span></div>
      <div className="grid grid-cols-4 gap-[14px]">
        {Array.from({ length: slots }).map((_, i) => urls[i] ? (
          <div key={i} className="relative aspect-square rounded-[14px] overflow-hidden bg-black" data-testid={`${testid}-tile-${i}`}>
            <img src={urls[i]} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="absolute top-2 left-2 sx-mono text-[9px] px-2 py-0.5 rounded-full bg-black/80 text-white">PORTADA</span>}
            <button type="button" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/70 text-white text-xs" data-testid={`${testid}-remove-${i}`}>×</button>
          </div>
        ) : i === files.length ? (
          <button key={i} type="button" onClick={() => inp.current?.click()} {...drop.dropProps} data-testid={`${testid}-drop`}
            className="aspect-square rounded-[14px] border border-dashed grid place-items-center text-center text-[13px] font-bold text-[#FF6B5E]"
            style={{ borderColor: drop.dragging ? CORAL : "rgba(255,68,56,.55)", background: drop.dragging ? "rgba(255,68,56,.08)" : "transparent" }}>
            <span>+<br />Arrastra o pega<br />(Ctrl+V)</span>
          </button>
        ) : <div key={i} className="aspect-square rounded-[14px] border border-dashed border-white/10" />)}
      </div>
      <input ref={inp} type="file" accept="image/*" multiple hidden onChange={(e) => { const picked = Array.from(e.target.files || []); setFiles((p) => [...p, ...picked].slice(0, max)); e.target.value = ""; }} data-testid={`${testid}-input`} />
    </DCard>
  );
};

export const Preview = ({ chip, title, meta, price, img }) => (
  <div><Lbl>Así se verá en el catálogo</Lbl>
    <div className="flex gap-3 p-3 rounded-[16px] border border-white/10 bg-[#101010]" data-testid="desk-preview">
      <div className="w-[70px] h-[98px] rounded-[8px] overflow-hidden shrink-0 grid place-items-center border border-dashed border-white/15 bg-black">
        {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <span className="sx-mono text-[9px] text-white/35 text-center">SIN<br />FOTO</span>}
      </div>
      <div className="min-w-0">
        <span className="inline-block sx-mono text-[9px] uppercase px-2 py-0.5 rounded-full border border-white/25 text-white/80">{chip}</span>
        <div className="text-[17px] font-bold text-white leading-tight mt-1" data-testid="desk-preview-title">{title || "Sin título"}</div>
        <div className="text-[12.5px] text-white/50">{meta}</div>
        <div className="text-[22px] font-extrabold text-[#FF4438]" data-testid="desk-preview-price">{money(price)}</div>
      </div>
    </div>
  </div>
);

export const usePreviewImg = (files) => {
  const [u, setU] = useState(null);
  useEffect(() => { if (!files[0]) { setU(null); return undefined; } const x = URL.createObjectURL(files[0]); setU(x); return () => URL.revokeObjectURL(x); }, [files]);
  return u;
};

export const Receive = ({ price, quantity, ctx }) => (parseFloat(price) > 0
  ? <SellerNetCards price={price} quantity={quantity} ship={ctx.shipRange} onConfigureShip={ctx.onShipEdit} />
  : null);

export const STEPS = [
  { n: "01", t: "Llenas y envías", s: "Una sola vez, con precio y fotos.", c: "#FF6B5E" },
  { n: "02", t: "Revisión < 12 h", s: "El equipo HIT la verifica.", c: "#fbbf24" },
  { n: "03", t: "Se publica sola", s: "Con tus datos. Te avisamos.", c: "#34D399" },
];
export const Steps = () => (
  <div className="space-y-3" data-testid="desk-steps">{STEPS.map((x) => (
    <div key={x.n} className="flex gap-3 items-start">
      <span className="w-9 h-9 rounded-[10px] border grid place-items-center sx-mono text-[11px] shrink-0" style={{ borderColor: x.c, color: x.c }}>{x.n}</span>
      <div><div className="text-[15px] font-bold text-white leading-tight">{x.t}</div><div className="text-[12.5px] text-white/50">{x.s}</div></div>
    </div>
  ))}</div>
);

// Botón principal: gris con "Te faltan N datos" y caja coral con la lista; al tocarlo lleva al primer error.
export const DPrimary = ({ missing, label, busy, onClick, onMissing, testid, loadingText = "Enviando…", onShip }) => {
  const n = missing.length;
  const shipOnly = onShip && n === 1 && missing[0] === "Envío";
  return (
    <div className="space-y-3">
      {n > 0 && (
        <div className="p-3 rounded-[14px] border text-[13px]" style={{ borderColor: "rgba(255,68,56,.6)", background: "rgba(255,68,56,.08)" }} data-testid={`${testid}-missing`}>
          <div className="font-bold text-[#FF6B5E]">{n > 1 ? `Te faltan ${n} datos` : "Te falta 1 dato"}</div>
          <div className="text-white/70">{missing.join(" · ")}</div>
        </div>
      )}
      <button type="button" onClick={shipOnly ? onShip : n ? onMissing : onClick} disabled={busy} data-testid={testid}
        className="w-full h-14 rounded-full text-[16px] font-bold transition-colors"
        style={n ? { background: "#1c1c1c", color: "rgba(255,255,255,.75)", border: "1px solid rgba(255,255,255,.12)" } : { background: CORAL, color: "#080808" }}>
        {busy ? loadingText : shipOnly ? "Configura tu envío" : n ? (n > 1 ? `Te faltan ${n} datos` : "Te falta 1 dato") : label}
      </button>
    </div>
  );
};

export const imgOf = imgUrl;
